import React from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Label,
} from 'recharts';

interface DataPoint {
  label: string;
  value: number;
  isReference?: boolean;
  referenceLabel?: string;
}

interface LineChartProps {
  data: DataPoint[];
  height?: number | string;
  width?: number | string;
  lineColor?: string;
  referenceLineColor?: string;
  referenceLabelColor?: string;
  referenceLabelBgColor?: string;  // New prop for label background
  strokeWidth?: number;
  // Add more customization options
  backgroundColor?: string;
  gridStrokeColor?: string;
  dotFillColor?: string;
  cursorColor?: string; // New prop for cursor color
  xAxisTitle?: string; // Added x-axis title prop
  yAxisTitle?: string; // Added y-axis title prop
}

const LineChartExample: React.FC<LineChartProps> = ({
  data,
  height = '100%',
  width = '100%',
  lineColor = '#00E4FF',
  referenceLineColor = 'rgba(255, 255, 255, 0.5)',
  referenceLabelColor = '#FFFFFF',
  strokeWidth = 2,
  gridStrokeColor = 'rgba(255, 255, 255, 0.3)',
  dotFillColor = '#000',
  cursorColor = '#A4A8AE', // Dashed line color for the tooltip cursor
  xAxisTitle,
  yAxisTitle,
}) => {
  // Process the data
  const processedData = React.useMemo(() => {
    if (!data || data.length === 0) return [];
    return data;
  }, [data]);
  
  // Calculate evenly spaced Y-axis ticks
  const calculateYAxisTicks = () => {
    if (!processedData || processedData.length === 0) return [];
    
    const values = processedData.map(item => item.value);
    const minValue = Math.floor(Math.min(...values));
    const maxValue = Math.ceil(Math.max(...values));
    
    // Define number of intervals (can be adjusted based on chart height)
    const intervalCount = 5;
    
    // Calculate the interval between ticks
    const interval = Math.ceil((maxValue - minValue) / intervalCount);
    
    // Generate evenly spaced ticks
    const ticks = [];
    for (let i = 0; i <= intervalCount; i++) {
      ticks.push(minValue + i * interval);
    }
    
    return ticks;
  };
  
  // Find reference point from processed data
  const referencePoint = processedData.find(point => point.isReference);
  
  // Generate the y-axis ticks
  const yAxisTicks = calculateYAxisTicks();
  
  // Custom dot component that only renders for reference points or other specific conditions
  const CustomDot = (props: any) => {
    const { cx, cy, payload } = props;
    
    // Only render the dot if this is a reference point or meets other criteria
    if (payload.isReference) {
      return (
        <circle 
          cx={cx} 
          cy={cy} 
          r={5}
          stroke={lineColor}
          strokeWidth={2}
          fill={dotFillColor}
        />
      );
    }
    // Don't render dots for other points
    return null;
  };
  
  // Custom cursor for the tooltip to create dotted vertical line
  const CustomCursor = (props: any) => {
    const { points, width, height, stroke } = props;
    const { x, y } = points[0];
    
    // Calculate spacing for dash segments
    const dashLength = 3; // Length of each dash
    const gapLength = 3; // Gap between dashes
    const segmentLength = dashLength + gapLength;
    const totalHeight = height;
    const numSegments = Math.floor(totalHeight / segmentLength);
    
    // Find the active point on the chart
    const activePoint = points[0];
    
    return (
      <g>
        {/* Create dashed line segments */}
        {Array.from({ length: numSegments }).map((_, index) => (
          <line
            key={index}
            x1={x}
            y1={y + index * segmentLength}
            x2={x}
            y2={y + index * segmentLength + dashLength}
            stroke={cursorColor}
            strokeWidth={1}
            opacity={0.9}
          />
        ))}
      </g>
    );
  };
  
  // Custom active dot component that creates the hover indicator shown in the image
  const CustomActiveDot = (props: any) => {
    const { cx, cy, stroke, dataKey, fill } = props;
    
    return (
      <g>
        {/* Outer circle */}
        <circle 
          cx={cx} 
          cy={cy} 
          r={5}  // Changed from 10 to 5 to make total width/height 10px
          fill={lineColor}
          // opacity={0.3}
        />
        {/* Inner circle */}
        <circle 
          cx={cx} 
          cy={cy} 
          r={2.5}  // Changed from 5 to 2.5 to maintain the same proportion
          fill="#FFFFFF"
        />
      </g>
    );
  };
  
  return (
    <ResponsiveContainer width={width} height={height}>
      <AreaChart
        data={processedData}
        margin={{ top: 30, right: 20, left: 10, bottom: 20 }}
      >
        <defs>
          <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={lineColor} stopOpacity={0.8} />
            <stop offset="50%" stopColor={lineColor} stopOpacity={0.3} />
            <stop offset="100%" stopColor={lineColor} stopOpacity={0.05} />
          </linearGradient>
        </defs>
        
        <CartesianGrid 
          strokeDasharray="3 3" 
          vertical={false} 
          stroke={gridStrokeColor} 
          horizontal={true}
        />
        
        <XAxis 
          dataKey="label"
          axisLine={false}
          tickLine={false}
          tick={{ fill: 'rgba(255, 255, 255, 0.8)', fontSize: xAxisTitle ? 14 : 12 }}
          stroke="rgba(255, 255, 255, 0.5)"
          padding={{ left: 10, right: 10 }}
          height={xAxisTitle ? 80 : 40}
          dy={10}
          label={xAxisTitle ? {
            value: xAxisTitle,
            position: "insideBottom",
            offset: 0,
            fill: "rgba(255, 255, 255)",
            fontSize: 16,
            fontWeight: 500,
            textAnchor: "middle",
          } : undefined}
        />
        
        <YAxis 
          axisLine={false}
          tickLine={false}
          tick={{ fill: 'rgba(255, 255, 255, 0.8)', fontSize: yAxisTitle ? 14 : 12  }}
          stroke="rgba(255, 255, 255, 0.5)"
          scale="linear"
          {...(yAxisTitle && { width: 100 })}
          dx={-5}
          ticks={yAxisTicks}
          domain={[Math.min(...yAxisTicks), Math.max(...yAxisTicks)]}
          label={yAxisTitle ? {
            value: yAxisTitle,
            angle: -90,
            position: "insideLeft",
            style: { textAnchor: 'middle' },
            fill: "rgba(255, 255, 255)",
            fontSize: 16,
            fontWeight: 500,
            offset: 30,
          } : undefined}
        />
        
        <Tooltip 
          contentStyle={{
            backgroundColor: "#363441",
            borderColor: "#363441",
            outline: "none !important",
            color: "white",
            padding: "4px 8px",
            fontSize: "10px",
            minWidth: "auto",
            borderRadius: "5px",

          }}
          labelStyle={{ display: 'none' }}
          itemStyle={{ color: "white", fontSize: "10px", padding: "2px 0" ,fontWeight:600}}
          formatter={(value) => [
            `${value}`,
          ]}
          cursor={<CustomCursor />}
        />
        
        {referencePoint && (
          <ReferenceLine 
            x={referencePoint.label}
            stroke={referenceLineColor} 
            strokeDasharray="3 3"
          >
            <Label 
              value={referencePoint.referenceLabel} 
              position="top" 
              fill={referenceLabelColor}
              style={{ 
                fontSize: '12px',
                fontWeight: 500,
              }}
              offset={10}
            />
          </ReferenceLine>
        )}
        
        <Area 
          type="monotone" 
          dataKey="value" 
          stroke={lineColor} 
          strokeWidth={strokeWidth}
          fill="url(#colorValue)"
          dot={<CustomDot />}
          activeDot={<CustomActiveDot />}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default LineChartExample;
